import { Injectable, inject, signal } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { Observable, of } from 'rxjs';
import { catchError, finalize, shareReplay, tap } from 'rxjs/operators';
import {
  UserAccessMenu,
  UserAccessResponseDto,
} from '../models/system-settings/user-menu.model';
import { ApiSystemSettingsService } from './api/menu/api-menu.service';
import { MapperUserAccessMenuService } from '../mappers/mapper-user-menu.service';
import {
  normalizeRoutePath,
  resolveMenuPath,
} from '../constants/menu-route-access.constant';

export interface PagePermission {
  menuId: number;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
}

type MenuLoadState = 'idle' | 'loading' | 'loaded';

@Injectable({
  providedIn: 'root',
})
export class MenuPermissionService {
  private api = inject(ApiSystemSettingsService);
  private mapper = inject(MapperUserAccessMenuService);
  private cookieService = inject(CookieService);

  private permissionsMap = signal<Record<string, PagePermission>>({});
  private menuLoadState = signal<MenuLoadState>('idle');
  private pendingLoad$: Observable<UserAccessResponseDto | null> | null = null;
  private lastMenuResponse: UserAccessResponseDto | null = null;

  readonly menuGroups = signal<UserAccessMenu[]>([]);
  readonly isMenuLoaded = signal(false);

  // ================= Normal Functions =================

  constructor() { }

  /**
   * Loads user menus once and updates the permission cache.
   */
  public loadUserMenus(): Observable<UserAccessResponseDto | null> {
    if (this.menuLoadState() === 'loaded') {
      return of(this.lastMenuResponse);
    }

    if (this.menuLoadState() === 'loading' && this.pendingLoad$) {
      return this.pendingLoad$;
    }

    const userIdStr = this.cookieService.get('user_id');
    const userId = userIdStr ? parseInt(userIdStr, 10) : 0;

    if (userId <= 0) {
      this.applyMenuData(null);
      return of(null);
    }

    this.menuLoadState.set('loading');

    this.pendingLoad$ = this.api.getUserAccessMenu(userId).pipe(
      tap((data) => this.applyMenuData(data)),
      catchError((err) => {
        console.error('[MenuPermission] Failed to load user access menu:', err);
        this.applyMenuData(null);
        return of(null);
      }),
      finalize(() => {
        this.menuLoadState.set('loaded');
        this.isMenuLoaded.set(true);
        this.pendingLoad$ = null;
      }),
      shareReplay(1),
    );

    return this.pendingLoad$;
  }

  /**
   * Extracts and updates the permissions cache from the loaded navigation hierarchy.
   */
  public updatePermissionCache(menus: UserAccessMenu[]): void {
    const newMap: Record<string, PagePermission> = {};

    menus.forEach((group) => {
      group.children.forEach((child) => {
        if (child.submenu) {
          child.submenu.forEach((subItem) => {
            if (subItem.path) {
              newMap[normalizeRoutePath(subItem.path)] = {
                menuId: subItem.id,
                canCreate: subItem.canCreate,
                canUpdate: subItem.canUpdate,
                canDelete: subItem.canDelete,
              };
            }
          });
        } else if (child.path) {
          newMap[normalizeRoutePath(child.path)] = {
            menuId: child.id,
            canCreate: true,
            canUpdate: true,
            canDelete: true,
          };
        }
      });
    });

    this.permissionsMap.set(newMap);
  }

  /**
   * Checks whether the current user can access a route based on loaded menus.
   */
  public canAccessRoute(path: string): boolean {
    const allowedPaths = Object.keys(this.permissionsMap());
    const menuPath = resolveMenuPath(path, allowedPaths);
    return this.getPermissions(menuPath).menuId > 0;
  }

  /**
   * Retrieves permissions and menuId for a specific URL path.
   */
  public getPermissions(path: string): PagePermission {
    const key = normalizeRoutePath(path);
    return this.permissionsMap()[key] || {
      menuId: 0,
      canCreate: false,
      canUpdate: false,
      canDelete: false,
    };
  }

  public getMenuId(path: string): number {
    return this.getPermissions(path).menuId;
  }

  public canCreate(path: string): boolean {
    return this.getPermissions(path).canCreate;
  }

  public canUpdate(path: string): boolean {
    return this.getPermissions(path).canUpdate;
  }

  public canDelete(path: string): boolean {
    return this.getPermissions(path).canDelete;
  }

  /** First accessible route based on sidebar menu order */
  public getFirstMenuRoute(): string | null {
    for (const group of this.menuGroups()) {
      for (const child of group.children) {
        if (child.submenu?.length) {
          for (const sub of child.submenu) {
            if (sub.path && this.getPermissions(sub.path).menuId > 0) {
              return sub.path;
            }
          }
        } else if (child.path && this.getPermissions(child.path).menuId > 0) {
          return child.path;
        }
      }
    }
    return null;
  }

  /** Landing route after login — first menu the user can access */
  public getDefaultLandingRoute(): string {
    return this.getFirstMenuRoute() ?? '/access-denied';
  }

  /** Clears cached menus and permissions — call on logout */
  public resetSession(): void {
    this.permissionsMap.set({});
    this.menuGroups.set([]);
    this.menuLoadState.set('idle');
    this.isMenuLoaded.set(false);
    this.pendingLoad$ = null;
    this.lastMenuResponse = null;
  }

  private applyMenuData(data: UserAccessResponseDto | null): void {
    this.lastMenuResponse = data;
    const menus = data ? this.mapper.mapMenuFromDto(data.menus || []) : [];
    this.menuGroups.set(menus);
    this.updatePermissionCache(menus);
  }

  // ================= API Functions =================
  // (Menu loading handled via loadUserMenus)
}
